import { FC, useEffect, useState } from "react";
import Logo from "../../../assets/images/logo.svg";
import BotLogo from "../../../assets/images/bot-logo.svg";
import { Box, FormControl, Stack, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Link, useNavigate } from "react-router-dom";
import Google from "../../../assets/images/gmail-logo.svg";
import Facebook from "../../../assets/images/fb-logo.svg";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { CustomButton } from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import "./index.css";
import { adjustToggle } from "../../../utils/adjustToggle";
import {
  useSigninMutation,
  useValidateTokenMutation,
} from "../../../services/auth";
import { errorToast, successToast } from "../../../utils/toast";
import { useForm } from "react-hook-form";
import { setUser } from "../../../rtkSlices/metaDataSlice";
import { useDispatch } from "react-redux";
import keys from "../../../utils/keys";

const currentYear = new Date().getFullYear();

const Signin: FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [signin, result] = useSigninMutation();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [validateToken] = useValidateTokenMutation();
  const dispatch = useDispatch();
  const theme = useTheme();
  useEffect(() => {
    adjustToggle();
  }, []);

  const {
    register,
    resetField,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const handleVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    const usernameElem: any = document.getElementsByName("username")[0];
    usernameElem.addEventListener("input", function (e: EventListener) {
      setValue("username", usernameElem.value);
    });
    const passwordElem: any = document.getElementsByName("password")[0];
    passwordElem.addEventListener("input", function (e: EventListener) {
      setValue("password", passwordElem.value);
    });
  }, []);

  const getTokenValidated = async (token: string) => {
    const response: any = await validateToken({ token });
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
    } else {
      successToast("Welcome to HiiBo");
      dispatch(setUser(response.data.result));
      localStorage.setItem(
        "userInfo",
        JSON.stringify({
          "cognito:username": response.data.result["cognito:username"],
        })
      );
      navigate("/chat");
    }
  };

  const onSubmit = async (data: any) => {
    setLoader(true);
    const { username, password } = data;
    const body = {
      username,
      password,
    };
    const response: any = await signin(body);
    setLoader(false);
    if (response.error) {
      errorToast(response.message || response.error.data.message);
    } else {
      getTokenValidated(response.data.IdToken);
    }
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          marginTop: "3.5%",
        }}
      >
        <img src={BotLogo} alt="Logo" className="bot-logo" />
        <Grid
          container
          spacing={2}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Grid size={{ xs: 10, md: 3 }} textAlign={"center"}>
            <img
              src={Logo}
              alt="Logo"
              style={{
                width: "100%",
                height: "180px",
                margin: "10% 0px 15px 0px",
              }}
            />
            <div>
              <div className="welcomeText">Welcome 👋</div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={2}>
                  <FormControl variant="standard">
                    <TextInput
                      label="Email Address"
                      placeholder="Enter a valid email address or username"
                      ariaLabel="email"
                      iconHandler={() => resetField("username")}
                      register={register}
                      icon={
                        <HighlightOffIcon
                          sx={{ color: "#2A945F", fontSize: "20px" }}
                        />
                      }
                      required={true}
                      name="username"
                      borderColor={errors.username && "#e11c1cb3"}
                      helperText={errors.username ? "Required field" : ""}
                      shrink={watch("username") ? true : undefined}
                    />
                  </FormControl>
                  <FormControl variant="standard">
                    <TextInput
                      label="Password"
                      placeholder="Enter a valid password"
                      ariaLabel="username"
                      type={showPassword ? "text" : "password"}
                      iconHandler={handleVisibility}
                      register={register}
                      shrink={watch("password") ? true : undefined}
                      icon={
                        showPassword ? (
                          <VisibilityOffIcon
                            sx={{ color: "#2A945F", fontSize: "20px" }}
                          />
                        ) : (
                          <VisibilityIcon
                            sx={{ color: "#2A945F", fontSize: "20px" }}
                          />
                        )
                      }
                      borderColor={errors.password && "#e11c1cb3"}
                      required={true}
                      pattern={
                        /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/
                      }
                      name="password"
                      helperText={
                        errors.password
                          ? errors.password.type == "pattern"
                            ? `Password must contain at least 8 characters, at least 1 uppercase letter (A-X), at least 1 lowercase letter (a-z), at least 1 number (0-9) and at least 1 special character from this set: !@#$%^&*()-_=+[]{}|;:'",.<>?/.`
                            : "Required field"
                          : ""
                      }
                    />
                  </FormControl>
                  <Link className="forgot-password" to="/forgot-password">
                    Forgot Password?
                  </Link>
                  <CustomButton
                    text={loader && result.isLoading ? "" : "Login"}
                    disabled={!watch("username") || !watch("password")}
                  />
                  <div className="hr-div">
                    <hr />{" "}
                    <Typography component="div" className="hr-text">or sign in with</Typography>{" "}
                    <hr />
                  </div>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ marginTop: "9px !important" }}
                  >
                    <Grid size={{ xs: 12, md: 6 }} textAlign={"center"}>
                      <CustomButton
                        text={loader && !result.isLoading ? "" : "Google"}
                        startIcon={
                          loader && !result.isLoading ? undefined : (
                            <img
                              src={Google}
                              alt="google"
                              style={{ width: "20px", height: "20px" }}
                            />
                          )
                        }
                        submit={(e) => {
                          e.preventDefault();
                          setLoader(true);
                          window.location.replace(keys.baseUrl + "user/google");
                        }}
                        color={theme.palette.primary.main}
                        bgcolor="#34B47440"
                        height="45px"
                        width="100%"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} textAlign={"center"}>
                      <CustomButton
                        text={"Facebook"}
                        startIcon={
                          <img
                            src={Facebook}
                            alt="facebook"
                            style={{ width: "20px", height: "20px" }}
                          />
                        }
                        submit={(e) => e.preventDefault()}
                        color={theme.palette.primary.main}
                        bgcolor="#34B47440"
                        height="45px"
                        width="100%"
                      />
                    </Grid>
                  </Grid>
                  <Typography component="div" className="text">
                    Don't have an account?{" "}
                    <Link className="signin" to="/signup">
                      Sign up
                    </Link>
                  </Typography>
                </Stack>
              </form>
            </div>
          </Grid>
        </Grid>
      </Box>
      <div className="copyright-div">
        <Typography component="div" className="copyright">
          © {currentYear} ALL RIGHTS RESERVED
        </Typography>
      </div>
    </>
  );
};

export default Signin;
